import React from "react";
import { Button } from "@/components/ui/button";
import { useNetworkStatus, retryLastFailedRequest } from "@/utils/networkStatus";

export default function NetworkStatusBanner() {
  const {
    isOffline,
    isRateLimited,
    serverError,
    rateLimitSecondsRemaining,
    isRetrying,
    retryRequest,
  } = useNetworkStatus();

  const showRateLimit = !isOffline && isRateLimited;
  const showServerError = !isOffline && !showRateLimit && Boolean(serverError);
  const showRetry = !isOffline && Boolean(retryRequest);
  const retryDisabled = isRetrying || (showRateLimit && rateLimitSecondsRemaining > 0);

  if (!isOffline && !showRateLimit && !showServerError) {
    return null;
  }

  let message = "";
  let bannerClasses = "bg-[#324c48] text-white";

  if (isOffline) {
    message = "You are offline. Please check your internet connection.";
    bannerClasses = "bg-amber-600 text-white";
  } else if (showRateLimit) {
    message =
      rateLimitSecondsRemaining > 0
        ? `Too many requests. Please wait ${rateLimitSecondsRemaining}s before retrying.`
        : "Rate limit window ended. You can retry now.";
    bannerClasses = "bg-[#7d5a00] text-white";
  } else if (showServerError) {
    message = serverError?.message || "Something went wrong on the server. Please retry.";
    bannerClasses = "bg-[#8b1f1f] text-white";
  }

  const handleRetry = async () => {
    await retryLastFailedRequest();
  };

  return (
    <div className={`w-full px-4 py-3 ${bannerClasses}`} role="status" aria-live="polite">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-medium">{message}</p>
        {showRetry ? (
          <Button
            type="button"
            onClick={handleRetry}
            variant="outline"
            className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
            disabled={retryDisabled}
            id="network-status-banner-btn-retry"
          >
            {isRetrying ? "Retrying..." : "Retry"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
